import { Button, Col, Divider, Row } from "antd";
import { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../../context/app/context";
import ScaleContext from "../../../context/scale/context";
import ScaleContextProvider from "../../../context/scale/provider";
import { ICreateScale } from "../../../models/scale/request";
import { IStore } from "../../../models/store/response";
import EndPoints from "../../../services/end-points";
import { execute } from "../../../utils/helpers/execute";
import { createNamlieahStoreFilter } from "../../../utils/helpers/store-filters";
import FieldBuilder from "../../form-components/field-builder";
import Spin from "../../general/antd/spin";
import styles from "./style.module.scss";

/** The name becomes the downloaded file's name *and* the xlsx sheet title, and
 *  Excel rejects both these characters and anything past 31 chars. */
/** The file is written at exactly this many rows, and an empty slot's code is
 *  `99` plus a three-digit hotkey, which is what caps the device at 999. */
const SCALE_MIN_SLOTS = 1;
const SCALE_MAX_SLOTS = 999;
const SCALE_MIN_DEPARTMENT = 0;
const SCALE_MAX_DEPARTMENT = 99;

const SCALE_NAME_MAX_LENGTH = 31;
const SCALE_NAME_FORBIDDEN_CHARACTERS = /^[^[\]:*?/\\]*$/;

const Form = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { direction } = useContext(AppContext);

  const { details, actions, loading } = useContext(ScaleContext);
  const { id } = useParams();

  const [stores, setStores] = useState<IStore[]>([]);
  const [storesLoading, setStoresLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  const defaultValues = {
    name: details?.name ?? "",
    storeId: details?.store?.id,
    // No safe default on create: the export length comes straight from it.
    slotsCount: details?.slotsCount ?? undefined,
    department: details?.department ?? 99,
    description: details?.description ?? "",
    isActive: details?.isActive ?? true,
  };

  const methods = useForm<any>({ defaultValues });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    id && actions.getDetails(Number(id));
  }, [id]);

  useEffect(() => {
    reset(defaultValues);
  }, [details]);

  // A scale can only live in a namlieah store; anything else is rejected with 422.
  useEffect(() => {
    const getStores = async () => {
      try {
        setStoresLoading(true);
        const { data } = await EndPoints.store.getAllStores({
          light: true,
          perPage: -1,
          filters: [createNamlieahStoreFilter()],
        });
        setStores(data.data);
      } catch (error) {
      } finally {
        setStoresLoading(false);
      }
    };
    getStores();
  }, [direction]);

  const onSubmit = async (data: any) => {
    await execute({
      callback: async () => {
        setLoadingSubmit(true);
        if (details) {
          // `storeId` is immutable: it is not part of the update payload at all.
          await actions.updateScale(details.id, {
            name: data.name,
            slotsCount: Number(data.slotsCount),
            department: Number(data.department),
            description: data.description || null,
            isActive: data.isActive,
          });
        } else {
          await actions.createScale({
            name: data.name,
            storeId: data.storeId,
            slotsCount: Number(data.slotsCount),
            department: Number(data.department),
            description: data.description || null,
            isActive: data.isActive,
          } as ICreateScale);
        }
        navigate(-1);
      },
      fallback: (error) => {},
      finallyCallback: () => {
        setLoadingSubmit(false);
      },
      throwException: false,
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        className={styles.container}
        style={{ padding: "20px" }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div>
          <span className={styles.title}>
            {details ? t("update_scale") : t("add_scale")}
          </span>
          <Divider className={styles.divider} />
          {loading.includes("details") && id ? (
            <div style={{ height: "60vh" }}>
              <Spin />
            </div>
          ) : (
            <div>
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12}>
                  <FieldBuilder
                    name="name"
                    label={t("name")}
                    input={{ type: "text" }}
                    rules={{
                      required: true,
                      maxLength: SCALE_NAME_MAX_LENGTH,
                      pattern: {
                        value: SCALE_NAME_FORBIDDEN_CHARACTERS,
                        message: t("scale_name_forbidden_characters"),
                      },
                    }}
                  />
                </Col>

                {/* The store is fixed at creation: a scale never moves between stores. */}
                {!details && (
                  <Col xs={24} sm={24} md={12} lg={12}>
                    <FieldBuilder
                      name="storeId"
                      label={`${t("store")}`}
                      width="large"
                      rules={{ required: true }}
                      input={{
                        type: "select",
                        allowSearch: true,
                        loading: storesLoading,
                        options: stores?.map((store) => ({
                          label: store?.name as string,
                          value: store?.id as number,
                        })),
                      }}
                    />
                  </Col>
                )}

                {/* Lowering it under the highest slot in use is rejected with
                    `scale_capacity_below_used`. */}
                <Col xs={24} sm={24} md={12} lg={12}>
                  <FieldBuilder
                    name="slotsCount"
                    label={t("slots_count")}
                    input={{ type: "number" }}
                    rules={{
                      required: true,
                      min: SCALE_MIN_SLOTS,
                      max: SCALE_MAX_SLOTS,
                    }}
                  />
                </Col>

                {/* A device setting written on every row of the file. */}
                <Col xs={24} sm={24} md={12} lg={12}>
                  <FieldBuilder
                    name="department"
                    label={t("department")}
                    input={{ type: "number" }}
                    rules={{
                      required: false,
                      min: SCALE_MIN_DEPARTMENT,
                      max: SCALE_MAX_DEPARTMENT,
                    }}
                  />
                </Col>

                <Col xs={24} sm={24} md={24} lg={24}>
                  <FieldBuilder
                    name="description"
                    label={t("description")}
                    input={{ type: "text-area", rows: 4 }}
                    rules={{ required: false }}
                  />
                </Col>

                <Col xs={24} sm={24} md={12} lg={12}>
                  <FieldBuilder
                    name="isActive"
                    label={t("is_active")}
                    input={{ type: "switch" }}
                    rules={{ required: false }}
                  />
                </Col>
              </Row>
            </div>
          )}
        </div>

        <div>
          <Divider className={styles.divider} />
          <div className={styles.formFooter}>
            <Button
              onClick={() => {
                navigate(-1);
              }}
            >
              {t("cancel")}
            </Button>
            <Button loading={loadingSubmit} htmlType="submit" type="primary">
              {t("save")}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

const ScaleForm = () => {
  return (
    <ScaleContextProvider>
      <Form />
    </ScaleContextProvider>
  );
};

export default ScaleForm;
